import * as Sentry from "@sentry/browser";
import { z } from "zod";
import { config } from "./config";
import { getPortalConfig, setPortalConfig } from "./config/portalConfig";
import {
  CACHED_COUNTRY_KEY,
  getStorageData,
  setLocalStorage,
} from "./helpers/localStorage";
import { retry } from "./helpers/retry";

export const countryInfoSchema = z
  .object({
    countryISO2: z.string().min(1),
  })
  .passthrough();

export type CountryInfo = z.infer<typeof countryInfoSchema>;

export const COUNTRY_CODE_KEY = "countryISO2";

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

type CachedCountryData = {
  countryISO2: string;
  timestamp: number;
};

export function getCachedCountryCode(): string | null {
  try {
    const cachedData = getStorageData(CACHED_COUNTRY_KEY);
    if (!cachedData) return null;

    const parsed = JSON.parse(cachedData) as CachedCountryData;
    return Date.now() - parsed.timestamp < ONE_WEEK_MS
      ? parsed.countryISO2
      : null;
  } catch (err) {
    console.error("Error parsing cached country data:", err);
    return null;
  }
}

export function getCountryCode(): Promise<string | null> {
  const countryCode = getPortalConfig(COUNTRY_CODE_KEY);
  if (!countryCode) {
    return setCountryConfig();
  }
  return Promise.resolve(countryCode);
}

export async function setCountryConfig(): Promise<string | null> {
  const cachedCountryISO2 = getCachedCountryCode();
  if (cachedCountryISO2) {
    setPortalConfig(COUNTRY_CODE_KEY, cachedCountryISO2);
    return cachedCountryISO2;
  }

  try {
    const countryData = await retry(
      () =>
        window
          .fetch(`${config.MFE_CAT_URL}/country`)
          .then((res) => res.json())
          .then((data): CountryInfo => countryInfoSchema.parse(data)),
      2
    );

    const countryCode = countryData.countryISO2;

    const cacheData: CachedCountryData = {
      countryISO2: countryCode,
      timestamp: Date.now(),
    };
    setLocalStorage(CACHED_COUNTRY_KEY, JSON.stringify(cacheData));
    setPortalConfig(COUNTRY_CODE_KEY, countryCode);
    return countryCode;
  } catch (err) {
    Sentry.captureException(err, {
      tags: {
        operation: "fetch_country_data",
      },
    });
    console.error("Error fetching country data:", err);
    setPortalConfig(COUNTRY_CODE_KEY, "");
    return null;
  }
}
